import { Selector } from '@ngxs/store';
import { CustomerProductTradeInState, TradeInStateModel } from './customer-product-trade-in.state';
import {
  AcceptTradeInQuoteResponse,
  TradeInProduct,
  TradeInResponse,
  VendorSubsidyResponse,
} from '@app/sales/models/trade-in-product.model';
import { GradingQuestion, StorageCapacityAlternative } from '@app/sales/modules/mobilephone/models/trade-in-api.model';
import { TradeInImeiErrorType } from '@app/sales/modules/mobilephone/models/trade-in-enums';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { CustomerProductMbbSelectors } from '@app/sales/modules/mbb/states/customer-product-mbb.selectors';
import { MbbProduct } from '@app/sales/modules/mbb/models/mbb-product.model';
import { VoiceProduct } from '@app/shared/models/voice-product.model';

export class TradeInSelectors {
  @Selector([CustomerProductTradeInState])
  static selectedStep(state: TradeInStateModel): number {
    return state.selectedStep;
  }

  @Selector([CustomerProductTradeInState])
  static interactedSteps(state: TradeInStateModel): number[] {
    return state.interactedSteps;
  }

  @Selector([CustomerProductTradeInState])
  static tradeInProduct(state: TradeInStateModel): TradeInProduct {
    return state.tradeIn;
  }

  @Selector([CustomerProductTradeInState])
  static quote(state: TradeInStateModel): AcceptTradeInQuoteResponse {
    return state.quote;
  }

  @Selector([CustomerProductTradeInState])
  static deviceModel(state: TradeInStateModel): string {
    return state.tradeIn?.specifications.deviceModel;
  }

  @Selector([CustomerProductTradeInState])
  static validateTradeInImeiError(state: TradeInStateModel): TradeInImeiErrorType {
    return state.tradeIn?.specifications.validateTradeInImeiError;
  }

  @Selector([CustomerProductTradeInState])
  static isValidatingImei(state: TradeInStateModel): boolean {
    return state.isValidatingImei;
  }

  @Selector([CustomerProductTradeInState])
  static previouslyValidatedImei(state: TradeInStateModel): string {
    return state.tradeIn?.specifications.imei;
  }

  @Selector([CustomerProductTradeInState])
  static storageCapacityAlternatives(state: TradeInStateModel): StorageCapacityAlternative[] {
    return state.tradeIn?.specifications.storageCapacityAlternatives || [];
  }

  @Selector([CustomerProductTradeInState])
  static selectedStorageCapacity(state: TradeInStateModel): StorageCapacityAlternative {
    return state.tradeIn?.specifications.selectedStorageCapacity;
  }

  @Selector([CustomerProductTradeInState])
  static gradingQuestions(state: TradeInStateModel): GradingQuestion[] {
    return state.tradeIn?.gradingQuestions || [];
  }

  @Selector([CustomerProductTradeInState])
  static tradeInVoucher(state: TradeInStateModel): TradeInResponse {
    return state.tradeIn?.tradeInVoucher;
  }

  @Selector([CustomerProductTradeInState])
  static isCalculatingTradeInValue(state: TradeInStateModel): boolean {
    return state.isCalculatingTradeInValue;
  }

  @Selector([CustomerProductTradeInState])
  static isCalculatingVendorSubsidy(state: TradeInStateModel): boolean {
    return state.isCalculatingVendorSubsidy;
  }

  @Selector([CustomerProductTradeInState])
  static estimatedPrice(state: TradeInStateModel): number {
    return state.tradeIn?.tradeInVoucher?.estimatedPrice;
  }

  @Selector([CustomerProductTradeInState])
  static iceSubsidyForZeroPrice(state: TradeInStateModel): number {
    return state.tradeIn?.tradeInVoucher?.iceSubsidyForZeroPrice;
  }

  @Selector([CustomerProductTradeInState])
  static deviceDisplayName(state: TradeInStateModel): string {
    return state.tradeIn?.specifications.deviceDisplayName;
  }

  @Selector([CustomerProductTradeInState])
  static imei(state: TradeInStateModel): string {
    return state.tradeIn?.specifications.imei;
  }

  @Selector([CustomerProductTradeInState])
  static vendorSubsidy(state: TradeInStateModel): VendorSubsidyResponse {
    return state.vendorSubsidy;
  }

  @Selector([CustomerProductTradeInState])
  static totalPrice(state: TradeInStateModel): number {
    const estimatedPrice = this.estimatedPrice(state);
    const iceSubsidyForZeroPrice = this.iceSubsidyForZeroPrice(state);
    const vendorSubsidyVoucherAmount = this.vendorSubsidy(state)?.voucherAmount.amountIncludingVAT || 0;

    return estimatedPrice + vendorSubsidyVoucherAmount + iceSubsidyForZeroPrice;
  }

  @Selector([CustomerProductTradeInState])
  static tradeInVoucherDisplayPrice(state: TradeInStateModel): number {
    const estimatedPrice = this.estimatedPrice(state);
    const iceSubsidyForZeroPrice = this.iceSubsidyForZeroPrice(state);

    return estimatedPrice + iceSubsidyForZeroPrice;
  }

  @Selector([CustomerProductTradeInState])
  static hasAcceptedTradeInVoucher(state: TradeInStateModel): boolean {
    return state.hasAcceptedTradeInVoucher;
  }

  @Selector([CustomerProductSelectors.getAllVoiceProducts, CustomerProductMbbSelectors.products])
  static isMissingSubscription(voiceProducts: VoiceProduct[], mbbProducts: MbbProduct[]): boolean {
    return (voiceProducts?.length ?? 0) + (mbbProducts?.length ?? 0) < 1;
  }

  @Selector([CustomerProductTradeInState])
  static isTradeInProductSelected(state: TradeInStateModel): boolean {
    return state.isTradeInProductSelected;
  }
}
